import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Two-layer PIN lock state for this console. Both layers are apps/web's own —
 * the field APK and the kiosks carry their own credentials and never read this.
 * - `appUnlocked` — the open PIN was entered. Persisted to sessionStorage
 *   so a reload within the same session doesn't re-prompt, but closing the app
 *   re-locks.
 * - `settingsUnlocked` — the admin PIN was entered to reach Settings. Kept
 *   in-memory only (not persisted), so it re-locks on reload and whenever the
 *   app itself re-locks — Settings is the more sensitive layer.
 */
interface LockState {
  appUnlocked: boolean;
  settingsUnlocked: boolean;
  unlockApp: () => void;
  /** Lock everything (used on sign-out and the "Lock" action). */
  lockAll: () => void;
  unlockSettings: () => void;
  lockSettings: () => void;
}

export const useLockStore = create<LockState>()(
  persist(
    (set) => ({
      appUnlocked: false,
      settingsUnlocked: false,
      unlockApp: () => set({ appUnlocked: true }),
      lockAll: () => set({ appUnlocked: false, settingsUnlocked: false }),
      unlockSettings: () => set({ settingsUnlocked: true }),
      lockSettings: () => set({ settingsUnlocked: false }),
    }),
    {
      name: 'hadir.lock',
      storage: createJSONStorage(() => sessionStorage),
      // Only the app-open layer survives a reload; the admin layer always
      // re-locks (don't persist settingsUnlocked).
      partialize: (s) => ({ appUnlocked: s.appUnlocked }),
    }
  )
);
